import React from "react";
import SeoHead from "../components/SEO/SeoHead";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  useTheme,
  Avatar,
  IconButton,
  Divider,
} from "@mui/material";
import { motion } from "framer-motion";
import CodeIcon from "@mui/icons-material/Code";
import BrushIcon from "@mui/icons-material/Brush";
import SpeedIcon from "@mui/icons-material/Speed";
import SecurityIcon from "@mui/icons-material/Security";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import GitHubIcon from "@mui/icons-material/GitHub";
import LinkedInIcon from "@mui/icons-material/LinkedIn";

interface ValueCard {
  title: string;
  description: string;
  icon: React.ReactNode;
}

const values: ValueCard[] = [
  {
    title: "Technische Exzellenz",
    description:
      "Ich nutze moderne Technologien und Best Practices für nachhaltige, sichere Weblösungen.",
    icon: <CodeIcon sx={{ fontSize: 40 }} />,
  },
  {
    title: "Individuelle Entwicklung",
    description:
      "Deine Website wird individuell gestaltet – benutzerfreundlich, stilvoll und markenkonform.",
    icon: <BrushIcon sx={{ fontSize: 40 }} />,
  },
  {
    title: "Maximale Performance",
    description:
      "Kurze Ladezeiten und saubere Umsetzung für beste Ergebnisse auf allen Geräten.",
    icon: <SpeedIcon sx={{ fontSize: 40 }} />,
  },
  {
    title: "Sicher & DSGVO-konform",
    description:
      "Ich lege Wert auf Datenschutz und sichere Standards – für dich und deine Nutzer.",
    icon: <SecurityIcon sx={{ fontSize: 40 }} />,
  },
  {
    title: "Zukunftsorientiert",
    description:
      "Ich entwickle skalierbare Lösungen, die auch morgen noch mit deinem Business wachsen.",
    icon: <RocketLaunchIcon sx={{ fontSize: 40 }} />,
  },
  {
    title: "Kreativer Ansatz",
    description:
      "Mit innovativen Ideen und klarer Strategie erschaffe ich digitale Erlebnisse mit Wirkung.",
    icon: <LightbulbIcon sx={{ fontSize: 40 }} />,
  },
];

const About: React.FC = () => {
  const theme = useTheme();

  return (
    <Box
      component="main"
      sx={{
        py: { xs: 8, md: 12 },
        minHeight: "100vh",
        backgroundColor:
          theme.palette.mode === "dark" ? "background.default" : "grey.50",
      }}
    >
      <SeoHead 
        title="Über pyzon | Website & Webentwicklung aus Darmstadt"
        description="Lernen Sie pyzon kennen - Ihre Website-Agentur in Darmstadt mit über 10 Jahren Erfahrung in der Webentwicklung. Technische Exzellenz und individuelle Lösungen für Ihren digitalen Erfolg."
        keywords="Website Agentur Darmstadt, Webentwickler Darmstadt, pyzon, Website erstellen Darmstadt, Website Agentur, Patrick Hutter"
        canonicalPath="/about"
      />

      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "2.5rem", md: "3.5rem" },
              fontWeight: 700,
              mb: 2,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              textAlign: "center",
            }}
          >
            Über pyzon
          </Typography>

          <Typography
            variant="h5"
            color="text.secondary"
            sx={{
              maxWidth: "800px",
              mx: "auto",
              mb: 8,
              textAlign: "center",
            }}
          >
            Seit 2018 entwickle und gestalte ich als selbstständiger
            Webentwickler maßgeschneiderte digitale Lösungen – kreativ,
            effizient und zukunftsorientiert. Ich kombiniere technisches
            Know-how mit einem Auge für Benutzerfreundlichkeit, um Unternehmen bei ihren
            digitalen Herausforderungen nachhaltig zu unterstützen.
          </Typography>

          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={4} key={value.title}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  style={{ height: "100%" }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      p: 4,
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(255, 255, 255, 0.8)",
                      backdropFilter: "blur(10px)",
                      border: `1px solid ${theme.palette.divider}`,
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0 8px 24px rgba(0,0,0,0.2)"
                            : "0 8px 24px rgba(0,0,0,0.1)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 3,
                        mx: "auto",
                        background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                        color: "white",
                        boxShadow: `0 4px 20px ${theme.palette.primary.main}40`,
                      }}
                    >
                      {value.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        textAlign: "center",
                        height: "64px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {value.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{
                        textAlign: "center",
                        flexGrow: 1,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {value.description}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 12 }} />

          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={4}>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8 }}
              >
                <Box
                  sx={{
                    position: "relative",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: -2,
                      left: -2,
                      right: -2,
                      bottom: -2,
                      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                      borderRadius: "50%",
                      zIndex: 0,
                    },
                  }}
                >
                  <Avatar
                    src="/patrick_hutter.png"
                    alt="Patrick Hutter"
                    sx={{
                      width: { xs: 280, md: 320 },
                      height: { xs: 280, md: 320 },
                      border: `4px solid ${theme.palette.background.paper}`,
                      position: "relative",
                      zIndex: 1,
                    }}
                  />
                </Box>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={8}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Typography variant="h2" gutterBottom>
                  B.Sc. Patrick Hutter
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: "primary.main",
                    mb: 3,
                  }}
                >
                  Gründer & Entwickler
                </Typography>
                <Typography variant="body1" paragraph>
                  Mit über 10 Jahren Erfahrung in der Webentwicklung habe ich
                  mich darauf spezialisiert, maßgeschneiderte digitale Lösungen
                  zu entwickeln, die nicht nur technisch einwandfrei sind,
                  sondern auch benutzerfreundlich und ästhetisch ansprechend.
                </Typography>
                <Typography variant="body1" paragraph>
                  Meine Leidenschaft ist es, Unternehmen dabei zu helfen, ihr
                  volles digitales Potenzial auszuschöpfen und ihre
                  Online-Präsenz auf das nächste Level zu heben.
                </Typography>
                <Box sx={{ mt: 4 }}>
                  <IconButton
                    href="https://github.com/phpyzon"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      mr: 2,
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.05)",
                      "&:hover": {
                        background: `${theme.palette.primary.main}20`,
                      },
                    }}
                  >
                    <GitHubIcon />
                  </IconButton>
                  <IconButton
                    href="https://www.linkedin.com/in/patrick-hutter-415078150/"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      mr: 2,
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.05)",
                      "&:hover": {
                        background: `${theme.palette.primary.main}20`,
                      },
                    }}
                  >
                    <LinkedInIcon />
                  </IconButton>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default About;
